import type { ReactNode } from 'react';
import React, { useState, useEffect } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import shortId from 'shortid';
import { isString, noop } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import { getFormattedLabel } from 'Components/utils/form/form.util';
import { ICONS } from 'Components/utils/icon/icons';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { themes } from './InputSwitch.themes';
type Props = {
  ariaDescribedby?: string;
  ariaLabelledby?: string;
  className?: string;
  color?: Colors;
  dataTestId?: string;
  iconChecked?: IconName;
  iconUnChecked?: IconName;
  id?: string;
  inputTestId?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isSwitch?: boolean;
  label?: string | Node;
  name?: string;
  onChange?: (name: string, checked: boolean, value: string | boolean) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  theme?: keyof typeof themes;
  validationState?: string;
  value?: string | boolean;
};
type State = {
  isChecked: boolean;
};
export function InputSwitch({
  ariaDescribedby,
  ariaLabelledby,
  className,
  color = COLORS.SAFFRON,
  dataTestId,
  iconChecked = ICONS.CHECK,
  iconUnChecked = null,
  id: idProp,
  inputTestId,
  isChecked: isCheckedProp = false,
  isDisabled = false,
  isLoading = false,
  isReadOnly,
  isRequired,
  isSwitch,
  label,
  name,
  onChange: onChangeProp = noop,
  onClick,
  theme = 'default',
  validationState = 'NOT_VALIDATED',
  value = '',
}: Props) {
  const [isChecked, setIsChecked] = useState(isCheckedProp);

  useEffect(() => {
    if (isCheckedProp !== isChecked) {
      setIsChecked(isCheckedProp);
    }
  }, [isCheckedProp]);

  const id = idProp ?? `InputSwitch_${shortId.generate()}`;

  function handleChange() {
    if (!isReadOnly && !isDisabled && !isLoading) {
      const nextChecked = !isChecked;
      setIsChecked(nextChecked);
      onChangeProp(name, nextChecked, value);
    }
  }

  function getIcon(): IconName {
    return isChecked ? iconChecked : iconUnChecked;
  }

  const { thChecked, thLabel, thRoot, thSwitch, thSwitchToggler } = themes[theme]({
    isChecked,
    ariaDescribedby,
    ariaLabelledby,
    className,
    color,
    dataTestId,
    iconChecked,
    iconUnChecked,
    id: idProp,
    inputTestId,
    isDisabled,
    isLoading,
    isReadOnly,
    isRequired,
    isSwitch,
    label,
    name,
    onChange: onChangeProp,
    onClick,
    theme,
    validationState,
    value,
  });
  return (
    <div data-testid={dataTestId} className={classnames(thRoot, className)}>
      {isSwitch ? (
        <button
          aria-checked={isChecked}
          aria-describedby={ariaDescribedby}
          aria-disabled={isDisabled}
          aria-labelledby={ariaLabelledby}
          className={thSwitch}
          data-testid={id}
          onClick={handleChange}
          role="checkbox"
          type="button"
        >
          <span className={thSwitchToggler}>
            <Icon
              className={classnames({
                'opacity-0 transition-opacity': isChecked,
              })}
              name={ICONS.FAT_DOT}
              width="0.5rem"
            />
          </span>
        </button>
      ) : (
        <button
          aria-checked={isChecked}
          aria-describedby={ariaDescribedby}
          aria-disabled={isDisabled}
          aria-labelledby={ariaLabelledby}
          className={thChecked}
          data-testid={id}
          disabled={isDisabled}
          onClick={handleChange}
          role="checkbox"
          type="button"
        >
          <Icon name={getIcon()} width="1em" />
        </button>
      )}
      <input
        checked={isChecked}
        className="hidden"
        disabled={isDisabled}
        id={id}
        data-testid={inputTestId}
        label={label}
        name={name}
        onChange={handleChange}
        onClick={onClick}
        required={isRequired}
        type="checkbox"
        value={value}
      />
      {isLoading && !isDisabled && !isReadOnly && <Spinner size="small" />}
      {!!label && (
        <label className={thLabel} htmlFor={id}>
          {isString(label) ? getFormattedLabel(label, isRequired) : label}
        </label>
      )}
    </div>
  );
}
